import React, { useEffect, useMemo, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { SectionHeading, Subheading as SubheadingBase } from "./Headings";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { loadStocksFromFirestore as loadStocksFromFirestoreAction } from "services/actions";
import useServedLocations from "hooks/useServedLocations";
import EmailIllustrationSrc from "../images/images/woman-smiling-and-eating-outside-the-pop-porium-shop-2434048.jpg";

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)(props => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);

const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`,
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(SectionHeading)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`

const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`
const Input = tw.input`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`
const Select = tw.select`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-primary-500`
const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8`

const normalizeValue = (value = "") => String(value ?? "").replace(/[_\s]+/g, "").toLowerCase();

const getFieldValue = (item, fields = []) => {
  for (const field of fields) {
    const value = item?.[field];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value);
    }
  }
  return "";
};

// For Firestore categories: use firestoreId as the option value (foods reference categories by firestoreId).
// For hardcoded fallback categories: use queryBy / title as the option value.
const getCategoryOptionValue = (item) =>
  item?.firestoreId || getFieldValue(item, ["queryBy", "category", "categoryName", "category_id", "name", "title"]);
const getCategoryOptionLabel = (item) =>
  getFieldValue(item, ["title", "name", "content", "label", "categoryName", "category"]);
// Returns the slug/key suitable for marketplace URL cat= param.
const getCategoryQueryKey = (item) =>
  getFieldValue(item, ["queryBy", "title", "name"]);

const TwoColBuyFormWithIllustrationFullForm = ({
  subheading = "BUY A FRESH FOOD ITEM",
  heading = <>Choose an item <span tw="text-green-500">to buy</span><wbr/> from us.</>,
  description = "All fields are required.",
  submitButtonText = "Buy Now",
  formAction = "#",
  formMethod = "post",
  textOnLeft = true,
  categories = [],
  foods = [],
  history,
  location,
  loadStocksFromFirestore,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedFood, setSelectedFood] = useState("");

  // Admin-managed served states & their neighborhoods (state → neighborhoods)
  const { states, neighborhoodsByState, allNeighborhoods } = useServedLocations();
  const initialParams = new URLSearchParams(location?.search || "");
  const [selectedState, setSelectedState] = useState(initialParams.get("state") || "");
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(initialParams.get("neighborhood") || "");

  const selectedNeighborhoods = selectedState ? (neighborhoodsByState[selectedState] || []) : [];
  const neighborhoodOptions = selectedNeighborhoods.length > 0 ? selectedNeighborhoods : allNeighborhoods;

  const handleStateChange = (event) => {
    setSelectedState(event.target.value);
    setSelectedNeighborhood("");
  };

  useEffect(() => {
    if (typeof loadStocksFromFirestore === "function") {
      loadStocksFromFirestore();
    }
  }, []);

  const categoryOptions = useMemo(() => {
    const rawCategories = Array.isArray(categories) ? categories : [];
    const seen = new Set();
    return [...rawCategories]
      .map((cat) => {
        const value = getCategoryOptionValue(cat);
        const label = getCategoryOptionLabel(cat) || value;
        return value ? { value, label, raw: cat } : null;
      })
      .filter(Boolean)
      .filter((option) => {
        const key = normalizeValue(option.value);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [categories]);

  // Map: optionValue → raw category object, for resolving marketplace URL params.
  const categoryByValue = useMemo(() => {
    const map = {};
    categoryOptions.forEach(({ value, raw }) => { map[value] = raw; });
    return map;
  }, [categoryOptions]);

  // Returns the queryBy slug (or title) for the selected category — used in
  // marketplace URLs so the marketplace can filter correctly.
  const getMarketplaceCatParam = () => {
    const catObj = categoryByValue[selectedCategory];
    return catObj ? (getCategoryQueryKey(catObj) || selectedCategory) : selectedCategory;
  };

  const filteredFoods = useMemo(() => {
    if (!selectedCategory) return [];
    const allFoods = Array.isArray(foods) ? foods : [];

    // Firestore path: food.categories[] stores firestoreId references.
    // A Firestore category was selected when categoryByValue[selectedCategory].firestoreId exists.
    const selectedCatObj = categoryByValue[selectedCategory];
    const isFirestoreCategory = !!selectedCatObj?.firestoreId;

    if (isFirestoreCategory) {
      return allFoods.filter((food) => {
        // Primary: food has a categories[] array with firestoreId refs
        if (Array.isArray(food.categories)) {
          return food.categories.includes(selectedCategory);
        }
        // Fallback for foods without categories[]: text match on category queryBy/title
        const catKey = getCategoryQueryKey(selectedCatObj);
        const catNorm = normalizeValue(catKey || selectedCategory);
        return (
          normalizeValue(getFieldValue(food, ["title", "name", "content"])).includes(catNorm) ||
          normalizeValue(getFieldValue(food, ["category", "categoryName", "marketCategory"])).includes(catNorm)
        );
      });
    }

    // Hardcoded-fallback path: text-based matching
    const categoryNorm = normalizeValue(selectedCategory);
    return allFoods.filter((food) => {
      const foodTitle = normalizeValue(getFieldValue(food, ["title", "name", "content"]));
      const content = normalizeValue((food?.contentArray || []).join(" "));
      const catText = normalizeValue(getFieldValue(food, ["category", "categoryName", "marketCategory"]));
      return foodTitle.includes(categoryNorm) || content.includes(categoryNorm) || catText.includes(categoryNorm);
    });
  }, [foods, selectedCategory, categoryByValue]);

  const visibleFoods = filteredFoods.slice(0, 20);
  const hasMoreFoods = filteredFoods.length > visibleFoods.length;

  const foodOptions = [
    ...visibleFoods.map((food) => ({
      value: food?.firestoreId || food?.queryBy || food?.title || food?.content || "",
      label: food?.title || food?.content || food?.queryBy || "",
    })),
    ...(hasMoreFoods ? [{ value: "__see_more__", label: "See more in Marketplace →" }] : []),
  ];

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setSelectedFood("");
  };

  const handleFoodChange = (event) => {
    const value = event.target.value;
    if (value === "__see_more__") {
      if (history && typeof history.push === "function") {
        history.push(`/marketplace?page=stock&tab=Foods&cat=${encodeURIComponent(getMarketplaceCatParam())}`);
      }
      return;
    }
    setSelectedFood(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (history && typeof history.push === "function") {
      let url = "/marketplace?page=stock&tab=Foods";
      if (selectedState) {
        url += `&state=${encodeURIComponent(selectedState)}`;
      }
      if (selectedNeighborhood) {
        url += `&neighborhood=${encodeURIComponent(selectedNeighborhood)}`;
      }
      if (selectedCategory) {
        // Use queryBy slug for the URL, not the firestoreId
        url += `&cat=${encodeURIComponent(getMarketplaceCatParam())}`;
      }
      if (selectedFood && selectedFood !== "__see_more__") {
        url += `&food=${encodeURIComponent(String(selectedFood))}`;
      }
      history.push(url);
    }
  };

  return (
    <Container>
      <TwoColumn>
        <ImageColumn>
          <Image imageSrc={EmailIllustrationSrc} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>{heading}</Heading>
            {description && <Description>{description}</Description>}
            <Form action={formAction} method={formMethod} onSubmit={handleSubmit}>
              <Input type="email" name="email" placeholder="Your Email Address" required />
              <Input type="text" name="name" placeholder="Full Name" required />
              <Select name="state" required value={selectedState} onChange={handleStateChange}>
                <option value="">Select State</option>
                {states.map((s) => (
                  <option key={s.firestoreId || s.name} value={s.name}>{s.name}</option>
                ))}
              </Select>
              <Select name="neighborhood" required value={selectedNeighborhood} onChange={e => setSelectedNeighborhood(e.target.value)} disabled={!selectedState}>
                <option value="">
                  {!selectedState
                    ? "Select a state first"
                    : neighborhoodOptions.length === 0
                    ? "No neighborhoods for this state"
                    : "Select Neighborhood Area"}
                </option>
                {neighborhoodOptions.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </Select>
              <Select name="cat" required value={selectedCategory} onChange={handleCategoryChange}>
                <option value="">Select Company Category</option>
                {categoryOptions.map((category) => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </Select>
              {/* Food selector — loaded from Firestore (stocks_foods), filtered by selected
                  category via food.categories[]. Limited to 20; "See more" goes to marketplace. */}
              <Select name="prod_type" required value={selectedFood} onChange={handleFoodChange} disabled={!selectedCategory}>
                <option value="">
                  {!selectedCategory
                    ? "Select a category first"
                    : filteredFoods.length === 0
                    ? "No foods found for this category"
                    : "What Food Item Do You Sell?"}
                </option>
                {foodOptions.map((food) => (
                  <option key={food.value} value={food.value}>{food.label}</option>
                ))}
              </Select>
              <SubmitButton type="submit">{submitButtonText}</SubmitButton>
            </Form>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  // Categories from Firestore (stocks_category) hydrated via loadStocksFromFirestore.
  // Falls back to hardcoded getCategory() data from store2.js when Firestore is unavailable.
  categories: state?.TabCardGridData?.tabs?.Category ?? [],
  // Foods from Firestore (stocks_foods). Falls back to hardcoded Foods tab data.
  foods: state?.TabCardGridData?.marketFoods ?? state?.TabCardGridData?.tabs?.Foods ?? [],
});

const mapDispatchToProps = (dispatch) => ({
  loadStocksFromFirestore: () => dispatch(loadStocksFromFirestoreAction()),
});

const ConnectedBuyForm = connect(mapStateToProps, mapDispatchToProps)(TwoColBuyFormWithIllustrationFullForm);

export default withRouter(ConnectedBuyForm);